import { FileEditIcon, PlayIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ReviewReportCard } from "@/components/case/review-report-card";
import { LlmCallSheet } from "@/components/llm-call-sheet";
import { usePhoneJsonEditorSession } from "@/components/phone-json-editor/phone-json-editor-session";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  api,
  ApiError,
  type CostRollup,
  type PipelineRun,
  type PipelineStep,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { statusBadgeVariant } from "@/lib/case-format";
import { parseReviewOutput } from "@/lib/review-problem-images";
import { formatDurationMs } from "@/lib/utils";
import { useVersionLive } from "@/hooks/use-version-live";

const STALE_RUNNING_MS = 12 * 60 * 1000;

function formatUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "-";
  if (n === 0) return "$0";
  if (n < 0.0001) return `$${n.toExponential(2)}`;
  return `$${n.toFixed(4)}`;
}

function CostRollupBadge({ rollup }: { rollup: CostRollup | undefined }) {
  if (!rollup || rollup.stepCount === 0) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={<Badge variant="secondary" className="cursor-default" />}
      >
        费用 {formatUsd(rollup.totalUsd)} · {rollup.succeededSteps}/
        {rollup.stepCount} 步
      </TooltipTrigger>
      <TooltipContent>LLM 调用：{rollup.llmCallCount}</TooltipContent>
    </Tooltip>
  );
}

function DurationCell({ durationMs }: { durationMs: number | null | undefined }) {
  if (durationMs == null) {
    return <span className="text-muted-foreground">-</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger
        render={<span className="cursor-default text-muted-foreground" />}
      >
        {formatDurationMs(durationMs)}
      </TooltipTrigger>
      <TooltipContent>{durationMs} ms</TooltipContent>
    </Tooltip>
  );
}

function PipelineStepsTable({
  steps,
  onStepClick,
}: {
  steps: PipelineStep[];
  onStepClick: (step: PipelineStep) => void;
}) {
  return (
    <div className="max-h-96 overflow-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>步骤</TableHead>
            <TableHead>状态</TableHead>
            <TableHead>模型</TableHead>
            <TableHead>费用</TableHead>
            <TableHead>耗时</TableHead>
            <TableHead>错误</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {steps.map((step) => (
            <TableRow
              key={step.id}
              className="cursor-pointer"
              onClick={() => onStepClick(step)}
            >
              <TableCell className="font-medium">{step.stepKey}</TableCell>
              <TableCell>
                <Badge variant={statusBadgeVariant(step.status)}>
                  {step.status}
                </Badge>
              </TableCell>
              <TableCell className="max-w-[10rem] truncate text-muted-foreground">
                {step.model ?? "-"}
              </TableCell>
              <TableCell className="font-mono text-xs">
                {step.costUsd != null ? `$${step.costUsd.toFixed(6)}` : "-"}
              </TableCell>
              <TableCell>
                <DurationCell durationMs={step.durationMs} />
              </TableCell>
              <TableCell className="max-w-[14rem] truncate text-xs text-destructive">
                {step.status === "failed" ? (step.errorText ?? "") : ""}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function VersionPipelinePanel({
  caseId,
  versionId,
}: {
  caseId: string;
  versionId: string;
}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ["case", caseId],
    queryFn: () => api.getCase(caseId),
    enabled: auth.status === "authed",
  });
  const { setLiveByCall, liveByCall } = useVersionLive();

  const versions = detailQuery.data?.versions ?? [];
  const selectedVersion = useMemo(() => {
    return versions.find((v) => v.id === versionId) ?? null;
  }, [versions, versionId]);

  const versionStatus = selectedVersion?.status ?? null;
  const canLoadPackage =
    Boolean(selectedVersion?.phoneJsonR2Key) ||
    Boolean(selectedVersion?.hasPipeline) ||
    versionStatus === "published" ||
    versionStatus === "failed";

  const runsQuery = useQuery({
    queryKey: ["pipeline-runs", versionId],
    queryFn: () => api.listPipelineRuns(versionId!),
    enabled: auth.status === "authed" && Boolean(versionId),
    refetchInterval: 2500,
  });

  const latestPipeline = useMemo(() => {
    const runs = runsQuery.data?.runs ?? [];
    return (
      runs.find(
        (r) =>
          r.stage === "pipeline" ||
          r.stage === "B" ||
          r.stage === "translate",
      ) ?? null
    );
  }, [runsQuery.data]);

  const pipelineRunQuery = useQuery({
    queryKey: ["pipeline-run", latestPipeline?.id],
    queryFn: () => api.getPipelineRun(latestPipeline!.id),
    enabled: auth.status === "authed" && Boolean(latestPipeline?.id),
    refetchInterval: (q) => {
      const status = q.state.data?.run.status;
      if (status === "running" || status === "queued") return 1500;
      return 4000;
    },
  });

  const phoneJsonQuery = useQuery({
    queryKey: ["phone-json", versionId],
    queryFn: () => api.getPhoneJson(versionId!),
    enabled:
      auth.status === "authed" && Boolean(versionId) && canLoadPackage,
    refetchInterval: () => {
      const bStatus = pipelineRunQuery.data?.run.status;
      if (bStatus === "running" || bStatus === "queued") return 2000;
      return false;
    },
  });

  const mediaReadyQuery = useQuery({
    queryKey: ["media-readiness", versionId],
    queryFn: () => api.getMediaReadiness(versionId!),
    enabled:
      auth.status === "authed" && Boolean(versionId) && canLoadPackage,
    refetchInterval: 5000,
  });

  const reviewStep = useMemo(() => {
    const steps = pipelineRunQuery.data?.steps ?? [];
    return (
      steps.find(
        (s) => s.stepKey === "review" && s.status === "succeeded",
      ) ?? null
    );
  }, [pipelineRunQuery.data?.steps]);

  const reviewArtifactsQuery = useQuery({
    queryKey: ["pipeline-step-artifacts", reviewStep?.id],
    queryFn: () => api.getPipelineStepArtifacts(reviewStep!.id),
    enabled: auth.status === "authed" && Boolean(reviewStep?.id),
  });

  const reviewResult = useMemo(
    () => parseReviewOutput(reviewArtifactsQuery.data?.output),
    [reviewArtifactsQuery.data?.output],
  );

  const [inspectId, setInspectId] = useState<string | null>(null);
  const [inspectStep, setInspectStep] = useState<PipelineStep | null>(null);
  const [inspectOpen, setInspectOpen] = useState(false);
  const [continueBusy, setContinueBusy] = useState(false);
  const editor = usePhoneJsonEditorSession();

  const inspectLiveText = useMemo(() => {
    if (!inspectId) return "";
    return liveByCall[inspectId] ?? "";
  }, [inspectId, liveByCall]);

  const stepCallsQuery = useQuery({
    queryKey: ["llm-calls-step", inspectStep?.id],
    queryFn: () => api.listLlmCallsByStep(inspectStep!.id),
    enabled: inspectOpen && Boolean(inspectStep?.id),
    refetchInterval: (q) => {
      const list = q.state.data?.calls ?? [];
      if (list.some((c) => c.status === "running")) return 1500;
      return false;
    },
  });

  const inspectStepCalls = stepCallsQuery.data?.calls ?? [];

  if (auth.status === "loading" || detailQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (auth.status === "anon") return null;

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>未找到案件</AlertTitle>
        <AlertDescription>无法加载案件流水线。</AlertDescription>
      </Alert>
    );
  }

  const steps: PipelineStep[] = pipelineRunQuery.data?.steps ?? [];
  const costRollup = pipelineRunQuery.data?.costRollup;
  const activeRun: PipelineRun | null =
    pipelineRunQuery.data?.run ?? latestPipeline;
  const isTranslateRun = activeRun?.stage === "translate";

  const canRetry =
    latestPipeline != null &&
    (latestPipeline.status === "failed" ||
      latestPipeline.status === "waiting_human" ||
      latestPipeline.status === "running");

  const canEditPhoneJson = editor.canEdit;

  const openStep = (step: PipelineStep) => {
    setInspectStep(step);
    setInspectId(null);
    setInspectOpen(true);
  };

  const onContinue = async () => {
    if (!latestPipeline?.id || continueBusy) return;
    setContinueBusy(true);
    try {
      const result = await api.continuePipelineRun(latestPipeline.id);
      await queryClient.invalidateQueries({
        queryKey: ["pipeline-runs", versionId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["pipeline-run", latestPipeline.id],
      });
      await queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      toast.add({
        type: "success",
        title: result.stale
          ? "已清理卡住的步骤并重新调度"
          : "已重新调度运行",
        description:
          result.kicked === "timeout"
            ? "流水线还在启动，稍后再看状态。"
            : undefined,
      });
    } catch (err) {
      toast.add({
        type: "error",
        title: "继续失败",
        description: err instanceof ApiError ? err.code : "continue_failed",
      });
    } finally {
      setContinueBusy(false);
    }
  };

  const mediaReady = mediaReadyQuery.data ?? null;
  const assetStepsPending = steps.some(
    (s) =>
      (s.stepKey === "asset_plan" ||
        s.stepKey === "head_refs" ||
        s.stepKey === "gen_assets") &&
      s.status !== "succeeded",
  );
  const enrichPending = steps.some(
    (s) => s.stepKey === "enrich" && s.status !== "succeeded",
  );
  const waitingForEnrichGate =
    latestPipeline?.status === "waiting_human" &&
    Boolean(reviewStep) &&
    enrichPending;

  const stalledRunning = Boolean(
    activeRun &&
      activeRun.status === "running" &&
      steps.some((s) => {
        if (s.status !== "running") return false;
        const t = Date.parse(s.updatedAt);
        if (!Number.isFinite(t)) return true;
        return Date.now() - t >= STALE_RUNNING_MS;
      }),
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{isTranslateRun ? "翻译流水线" : "流水线"}</CardTitle>
          <CardDescription>
            {isTranslateRun ? (
              <>
                translate_json (validate + LLM fix) → extract_media →
                classify_images → translate_images → regen_audio → assemble。
                失败运行可在此重试。新语言包请在源 en 版本使用「创建翻译」。
              </>
            ) : (
              <>
                generate_phone → refine_phone → experience_guide → asset_plan → head_refs →
                gen_assets → review → 人工确认线索 → enrich → 再跑素材。在「元信息」页设置剧本 +
                元信息后启动。review 后需确认有效线索再继续 enrich。失败运行可在此重试。成功后重跑请使用「版本」→「创建版本」。
              </>
            )}
            {activeRun ? (
              <>
                {" "}
                运行{" "}
                <code className="text-xs">{activeRun.id.slice(0, 8)}…</code>{" "}
                <Badge variant={statusBadgeVariant(activeRun.status)}>
                  {activeRun.status}
                </Badge>
                {isTranslateRun ? (
                  <>
                    {" "}
                    <Badge variant="secondary">translate</Badge>
                  </>
                ) : null}
              </>
            ) : null}
          </CardDescription>
          {canRetry || (costRollup && costRollup.stepCount > 0) ? (
            <CardAction>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {canRetry ? (
                  <Button
                    disabled={continueBusy}
                    onClick={() => void onContinue()}
                  >
                    {continueBusy ? (
                      <Spinner />
                    ) : (
                      <HugeiconsIcon
                        icon={PlayIcon}
                        data-icon="inline-start"
                      />
                    )}
                    {continueBusy
                      ? "恢复中…"
                      : latestPipeline?.status === "running"
                        ? "恢复卡住的运行"
                        : latestPipeline?.status === "failed"
                          ? "重试失败运行"
                          : waitingForEnrichGate
                            ? "确认线索，继续 enrich"
                            : assetStepsPending
                              ? "继续素材步骤"
                              : "重试失败运行"}
                  </Button>
                ) : null}
                <CostRollupBadge rollup={costRollup} />
              </div>
            </CardAction>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {stalledRunning ? (
            <Alert variant="destructive">
              <AlertTitle>运行可能已卡住</AlertTitle>
              <AlertDescription>
                外层仍显示 running，但当前步骤超过 12 分钟没有进展。步骤里的调用可能已经失败。点击「恢复卡住的运行」会清掉卡住的步骤并重新调度。
              </AlertDescription>
            </Alert>
          ) : null}

          {steps.length === 0 ? (
            <Empty className="border border-border py-8">
              <EmptyHeader>
                <EmptyTitle>尚无流水线运行</EmptyTitle>
                <EmptyDescription>
                  {selectedVersion?.branchLabel?.startsWith("translate:")
                    ? "此语言版本尚无翻译运行。请在源 en 版本使用「创建翻译」启动。"
                    : "打开「元信息」页，设置剧本 + 元信息，然后启动流水线。"}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <PipelineStepsTable steps={steps} onStepClick={openStep} />
          )}

          {reviewResult ? (
            <ReviewReportCard
              result={reviewResult}
              caseId={caseId}
              versionId={versionId}
              canReplace={
                Boolean(versionId) && versionStatus !== "superseded"
              }
            />
          ) : null}
          {waitingForEnrichGate ? (
            <Alert>
              <AlertTitle>等待确认有效线索</AlertTitle>
              <AlertDescription>
                review 已完成。请对照小手机与审阅报告确认有效线索无误（可先手改
                phone JSON），再点击「确认线索，继续 enrich」。enrich
                只补日常数据，不会改已有线索；完成后会再跑一轮素材。
              </AlertDescription>
            </Alert>
          ) : null}

          {phoneJsonQuery.data?.phoneJson != null || canEditPhoneJson ? (
            <>
              <Separator />
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium">Phone JSON</p>
                      <Badge variant="secondary">
                        {phoneJsonQuery.data?.placeholderCount ?? 0}{" "}
                        个占位符
                      </Badge>
                      {mediaReady ? (
                        <Badge
                          variant={mediaReady.ok ? "success" : "warning"}
                        >
                          媒体{mediaReady.ok ? "已就绪" : "未就绪"}
                        </Badge>
                      ) : null}
                      {editor.dirty ? (
                        <Badge variant="warning">未保存</Badge>
                      ) : null}
                    </div>
                    {phoneJsonQuery.data?.phoneJsonR2Key ? (
                      <code className="truncate text-xs text-muted-foreground">
                        {phoneJsonQuery.data.phoneJsonR2Key}
                      </code>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        表单编辑在页头打开：按区块增删改，无需手写 JSON。
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => editor.setOpen(true)}
                  >
                    <HugeiconsIcon
                      icon={FileEditIcon}
                      data-icon="inline-start"
                    />
                    {canEditPhoneJson ? "打开表单编辑器" : "查看内容"}
                  </Button>
                </div>
              </div>
            </>
          ) : activeRun &&
            (activeRun.status === "running" ||
              activeRun.status === "queued") ? (
            <p className="text-sm text-muted-foreground">
              {isTranslateRun
                ? "正在翻译包与媒体…"
                : "正在生成草稿 Phone JSON 与素材…"}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <LlmCallSheet
        callId={inspectId}
        step={inspectStep}
        stepCalls={inspectStepCalls}
        stepCallsLoading={
          Boolean(inspectStep) &&
          (stepCallsQuery.isLoading ||
            (stepCallsQuery.isFetching && !stepCallsQuery.data))
        }
        open={inspectOpen}
        onOpenChange={(open) => {
          setInspectOpen(open);
          if (!open) {
            setInspectStep(null);
            setInspectId(null);
          }
        }}
        liveText={inspectLiveText}
        onSelectCall={(id) => setInspectId(id)}
        onRerun={(newCall) => {
          setLiveByCall((prev) => ({ ...prev, [newCall.id]: "" }));
          setInspectId(newCall.id);
          void queryClient.invalidateQueries({
            queryKey: ["llm-calls-step"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["pipeline-run"],
          });
        }}
      />
    </div>
  );
}
